import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';

import { formatCurrency, formatDate } from '../../utils/format';
import api from '../../services/api';

const RelatorioJurosPagos = () => {
  // Função para pegar data atual no formato YYYY-MM-DD
  const getToday = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [dataInicio, setDataInicio] = useState(getToday());
  const [dataFim, setDataFim] = useState(getToday());
  const [clienteId, setClienteId] = useState('');
  const [clientes, setClientes] = useState([]);
  const [relatorio, setRelatorio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Carregar lista de clientes
  useEffect(() => {
    const carregarClientes = async () => {
      try {
        const response = await api.get('/clientes');
        setClientes(response.data);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      }
    };
    carregarClientes();
  }, []);

  const gerarRelatorio = async () => {
    if (!dataInicio || !dataFim) {
      setError('Por favor, selecione as datas de início e fim');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = {
        data_inicial: dataInicio,
        data_final: dataFim
      };

      if (clienteId) {
        params.id_cliente = clienteId;
      }

      const response = await api.get('/relatorios/juros-pagos', { params });
      setRelatorio(response.data);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      setError('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const calcularTotalJuros = () => {
    return relatorio.reduce((total, item) => total + parseFloat(item.juros_pago || 0), 0);
  };

  const calcularTotalPago = () => {
    return relatorio.reduce((total, item) => total + parseFloat(item.valor_pago || 0), 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Relatório de Juros Pagos
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Início"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Fim"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Cliente (Opcional)</InputLabel>
              <Select
                value={clienteId}
                onChange={(e) => setClienteId(e.target.value)}
                label="Cliente (Opcional)"
              >
                <MenuItem value="">
                  <em>Todos os clientes</em>
                </MenuItem>
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              onClick={gerarRelatorio}
              disabled={loading || !dataInicio || !dataFim}
              fullWidth
              sx={{ height: 56 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Gerar Relatório'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {relatorio.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Resultados ({relatorio.length} registros)
            </Typography>
            <Box>
              <Typography variant="subtitle1">
                Total Juros: {formatCurrency(calcularTotalJuros())}
              </Typography>
              <Typography variant="subtitle1">
                Total Pago: {formatCurrency(calcularTotalPago())}
              </Typography>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Empréstimo</TableCell>
                  <TableCell>Parcela</TableCell>
                  <TableCell align="right">Juros Pago</TableCell>
                  <TableCell align="right">Valor Pago</TableCell>
                  <TableCell align="right">Principal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {relatorio.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatDate(item.data_pagamento)}</TableCell>
                    <TableCell>{item.nome_cliente}</TableCell>
                    <TableCell>{item.id_emprestimo}</TableCell>
                    <TableCell>{item.numero_parcela}</TableCell>
                    <TableCell align="right">{formatCurrency(item.juros_pago)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.valor_pago)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.valor_principal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioJurosPagos; 